import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, AlertTriangle, TrendingDown, Type, AlertOctagon, Receipt,
  ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';
import apiService from '../services/apiService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function TipoBadge({ tipo }) {
  if (tipo === 'margem_insuficiente')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded whitespace-nowrap">
        <TrendingDown size={11} />Margem insuficiente (antigo)
      </span>
    );
  if (tipo === 'venda_abaixo_politica')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded whitespace-nowrap">
        <Receipt size={11} />Venda abaixo da política
      </span>
    );
  if (tipo === 'custo_medio_zerado')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-800 text-white px-2 py-0.5 rounded whitespace-nowrap">
        <AlertOctagon size={11} />Erro grave: custo zerado
      </span>
    );
  if (tipo === 'nome_produto')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-800 px-2 py-0.5 rounded whitespace-nowrap">
        <Type size={11} />Descrição fora do padrão
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium bg-orange-100 text-orange-800 px-2 py-0.5 rounded whitespace-nowrap">
      <AlertTriangle size={11} />Preço zerado
    </span>
  );
}

function fmtDataHora(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function LogsErros() {
  const navigate = useNavigate();

  // Filtros
  const [filtroData,    setFiltroData]    = useState('');
  const [filtroTipo,    setFiltroTipo]    = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');

  // Dados
  const [logs,       setLogs]       = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [loading,    setLoading]    = useState(false);
  const [erro,       setErro]       = useState('');

  // ── Buscar ─────────────────────────────────────────────────────────────────
  const buscar = useCallback(async (page = 1) => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.logsErros.listar({
        page,
        limit: 20,
        data:    filtroData    || undefined,
        tipo:    filtroTipo    || undefined,
        usuario: filtroUsuario.trim() || undefined,
      });
      const d = resp.data;
      setLogs(d.data ?? []);
      setPagination(d.pagination ?? { total: 0, page: 1, limit: 20, pages: 1 });
    } catch {
      setErro('Erro ao carregar logs. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, [filtroData, filtroTipo, filtroUsuario]);

  // Carrega ao montar
  useEffect(() => { buscar(1); }, []); // eslint-disable-line

  function handleBuscar(e) {
    e.preventDefault();
    buscar(1);
  }

  function irPagina(p) {
    if (p < 1 || p > pagination.pages) return;
    buscar(p);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-6xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Log de Erros</h1>
          <p className="text-sm text-gray-500">Produtos com preço zerado ou margem insuficiente detectados no ERP</p>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleBuscar} className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Dia</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroData}
              onChange={e => setFiltroData(e.target.value)}
            />
          </div>

          <div className="min-w-[200px]">
            <label className="text-xs text-gray-500 block mb-1">Tipo de erro</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroTipo}
              onChange={e => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="preco_zerado">Preço zerado</option>
              <option value="margem_insuficiente">Margem insuficiente (antigo)</option>
              <option value="nome_produto">Descrição fora do padrão</option>
              <option value="venda_abaixo_politica">Venda abaixo da política</option>
              <option value="custo_medio_zerado">Erro grave: custo médio zerado</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-gray-500 block mb-1">Usuário do ERP</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Buscar por código ou nome..."
              value={filtroUsuario}
              onChange={e => setFiltroUsuario(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFiltroData('');
                setFiltroTipo('');
                setFiltroUsuario('');
                setTimeout(() => buscar(1), 0);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-1.5"
            >
              <RefreshCw size={13} />Limpar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
            >
              <Search size={14} />Buscar
            </button>
          </div>
        </div>
      </form>

      {/* Tabela */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

        {/* Totalizador */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-500">
            {loading ? 'Carregando...' : `${pagination.total} registro(s) encontrado(s)`}
          </span>
          {pagination.pages > 1 && (
            <span className="text-xs text-gray-400">
              Página {pagination.page} de {pagination.pages}
            </span>
          )}
        </div>

        {erro && (
          <div className="px-5 py-8 text-center text-sm text-red-600">{erro}</div>
        )}

        {!erro && !loading && logs.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            Nenhum erro encontrado com os filtros informados.
          </div>
        )}

        {!erro && logs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium">Produto</th>
                  <th className="text-left px-4 py-3 font-medium">Usuário ERP</th>
                  <th className="text-left px-4 py-3 font-medium">Motivo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map(l => (
                  <tr key={`${l.tipo}-${l.id}`} className="hover:bg-gray-50 transition-colors">

                    {/* Data */}
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-gray-900">{fmtDataHora(l.data_log_erp)}</div>
                    </td>

                    {/* Tipo */}
                    <td className="px-4 py-3">
                      <TipoBadge tipo={l.tipo} />
                    </td>

                    {/* Produto */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[220px]">
                        {l.nome_produto || '—'}
                      </div>
                      <div className="text-xs text-gray-400">Cód. {l.codigo_produto}</div>
                    </td>

                    {/* Usuário ERP */}
                    <td className="px-4 py-3">
                      <div className="text-gray-900 truncate max-w-[180px]">
                        {l.usuario_erp_nome || '—'}
                      </div>
                      {l.usuario_erp_codigo && (
                        <div className="text-xs text-gray-400">Cód. {l.usuario_erp_codigo}</div>
                      )}
                    </td>

                    {/* Motivo */}
                    <td className="px-4 py-3">
                      <span className="text-gray-600 text-xs truncate max-w-[320px] block" title={l.mensagem}>
                        {l.mensagem}
                      </span>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button
              onClick={() => irPagina(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />Anterior
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                let p;
                if (pagination.pages <= 7) {
                  p = i + 1;
                } else if (pagination.page <= 4) {
                  p = i + 1;
                } else if (pagination.page >= pagination.pages - 3) {
                  p = pagination.pages - 6 + i;
                } else {
                  p = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => irPagina(p)}
                    disabled={loading}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                      ${p === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => irPagina(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima<ChevronRight size={14} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
